import { useNavigate } from "react-router-dom";
import { useAwsConfig } from "../../../../../context/Dashboard/aws/contextConfig.js";
import { useThemeMode } from "../../../../../context/contextTheme.js";
import hexToRGBA from "../../../../../utils/hexToRGBA.js";

export default function MainTable() {
  const navigate = useNavigate();
  const { theme } = useThemeMode();
  const { cAwsConfigAll: data, setService } = useAwsConfig();
  const summary = data?.last_run?.summary;

  if (!summary || typeof summary !== "object") {
    return (
      <div className="text-center py-5 rounded-4 border" style={{ color: theme.textMuted, background: theme.background }}>
        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
        Parsing scan results…
      </div>
    );
  }

  const getSeverityStyle = (item) => {
    if (item.flagged_items > 0) return { color: "#dc3545", bg: hexToRGBA("#dc3545", 0.1), label: "Critical" };
    if (item.rules_count > 0) return { color: "#ffc107", bg: hexToRGBA("#ffc107", 0.1), label: "Warning" };
    return { color: "#198754", bg: hexToRGBA("#198754", 0.1), label: "Good" };
  };

  return (
    <div
      className="card border-0 rounded-4 shadow-sm overflow-hidden"
      style={{ background: theme.background, width: 900 }}>
      <div className="table-responsive">
        <table className="table align-middle mb-0" style={{ color: theme.textColor }}>
          <thead style={{ background: "#344266" }}>
            <tr className="text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>
              <th className="ps-4 py-3 border-0">Service Asset</th>
              <th className="text-center py-3 border-0">Resources</th>
              <th className="text-center py-3 border-0">Rules</th>
              <th className="text-center py-3 border-0">Findings</th>
              <th className="text-center py-3 border-0">Checks</th>
            </tr>
          </thead>

          <tbody>
            {Object.entries(summary).map(([service, item]) => {
              const isDisabled =
                item.resources_count === 0 &&
                item.rules_count === 0 &&
                item.flagged_items === 0 &&
                item.checked_items === 0;

              const status = getSeverityStyle(item);

              return (
                <tr
                  key={service}
                  className={`scout-row-v2 ${isDisabled ? "disabled-row" : "active-row"}`}
                  onClick={() => {
                    if (isDisabled) return;
                    setService(service);
                    navigate(`/dashboard/cloud-dashboard/aws/service/${service}`);
                  }}
                >
                  {/* Service Label */}
                  <td className="ps-4 py-3">
                    <div className="d-flex align-items-center">
                      <div
                        className="me-3 rounded-circle"
                        style={{ width: 8, height: 8, background: isDisabled ? "#dee2e6" : status.color }}
                      />
                      <div>
                        <div className="fw-bold text-capitalize" style={{ fontSize: "0.9rem" }}>{service}</div>
                        {!isDisabled && (
                          <span style={{ fontSize: "0.65rem", color: status.color, fontWeight: 700 }}>
                            {status.label}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Resource Count */}
                  <td className="text-center">
                    <span className="badge rounded-pill bg-light text-dark border fw-medium px-3">
                      {item.resources_count}
                    </span>
                  </td>

                  {/* Rules Count */}
                  <td className="text-center text-muted">
                    {item.rules_count}
                  </td>

                  {/* Findings (Flagged Items) */}
                  <td className="text-center">
                    <div
                      className="d-inline-block px-3 py-1 rounded-3 fw-bold"
                      style={{
                        backgroundColor: item.flagged_items > 0 ? status.bg : "transparent",
                        color: item.flagged_items > 0 ? status.color : theme.textMuted,
                        minWidth: "40px"
                      }}
                    >
                      {item.flagged_items}
                    </div>
                  </td>

                  {/* Checked Items */}
                  <td className="text-center pe-4" style={{ color: theme.textMuted, fontSize: "0.85rem" }}>
                    {item.checked_items}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <style>{`
        .scout-row-v2 {
          transition: all 0.2s ease;
          border-bottom: 1px solid ${hexToRGBA(theme.textColor, 0.05)};
        }

        .active-row:hover {
          background-color: ${hexToRGBA("#344266", 0.03)} !important;
          cursor: pointer;
        }

        .active-row:hover .fw-bold {
          color: #344266;
        }

        .disabled-row {
          opacity: 0.5;
          background-color: ${hexToRGBA(theme.textColor, 0.02)};
          cursor: not-allowed;
        }

        .table > :not(caption) > * > * {
          box-shadow: none !important;
        }

        /* Custom scrollbar for better UX */
        .table-responsive::-webkit-scrollbar {
          height: 6px;
        }
        .table-responsive::-webkit-scrollbar-thumb {
          background: #dee2e6;
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
}